import differenceInDays from 'date-fns/differenceInDays';

import { TUploadedFile } from '@src-common/api/definitions/w4d-upload-types';

import { prepareDate, parseDateOnly } from '../api/prepare-date';

type TUploadStatus = 'pending' | 'approved' | 'rejected' | 'expired';

const EXPIRATION_THRESHOLD_DAYS = 14;

interface UploadedFile<TDocType extends string> {
  docType: TDocType;
  createdAt: Date | null;
  expiresAt: Date | null;
  updatedAt: Date | null;
  previewUrl?: string;
  downloadUrl: string;
  docFormat: string;
  status: TUploadStatus;
  expiresSoon: boolean;
  rejectReason?: string;
}

const prepareExpiresSoon = (expiresAt: string | null | undefined) => {
  if (!expiresAt) {
    return false;
  }

  const expiryDate = parseDateOnly(expiresAt);

  if (!expiryDate) {
    return false;
  }

  const daysToExpiration = differenceInDays(expiryDate, Date.now());

  return daysToExpiration > 0 && daysToExpiration < EXPIRATION_THRESHOLD_DAYS;
};

const prepareUploadedFile = <TDocType extends string>(
  rawUpload: TUploadedFile,
): UploadedFile<TDocType> => ({
  docType: rawUpload.type as TDocType,
  createdAt: prepareDate(rawUpload.created_at),
  expiresAt: parseDateOnly(rawUpload.expires_at), // UTC 00:00
  expiresSoon: prepareExpiresSoon(rawUpload.expires_at),
  updatedAt: prepareDate(rawUpload.updated_at),
  previewUrl: rawUpload.preview_url,
  downloadUrl: rawUpload.url,
  docFormat: rawUpload.format,
  status: rawUpload.status as TUploadStatus,
  rejectReason: rawUpload.reject_reason,
});

export type { UploadedFile, TUploadStatus };
export { prepareUploadedFile };
